import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import api from '../../utils/api';
import { useDidStream } from '../../hooks/useDidStream';
import { LiveAvatar } from '../../components/interview/LiveAvatar';
import { toast } from '../../hooks/use-toast';
import {
  Video, Mic, StopCircle, Play,
  Volume2, Send, Timer,
  Loader2, CheckCircle, AlertCircle,
  Award, BarChart3, RefreshCw,
  Upload, FileText, Sparkles, Camera,
  CreditCard, Lock, Coins
} from 'lucide-react';
import { AI_PERSONAS } from '../../constants/aiPersonas';

const StructuredInterview = () => {
  const navigate = useNavigate();
  
  // Access control
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [credits, setCredits] = useState(0);
  const [creditsRequired] = useState(25); // Interview costs 25 credits (~$20)
  const [loading, setLoading] = useState(false);

  // DS-160 state
  const [ds160Data, setDs160Data] = useState(null);
  const [ds160Content, setDs160Content] = useState('');
  const [ds160File, setDs160File] = useState(null);
  const [ds160Analyzed, setDs160Analyzed] = useState(false);
  const [analyzingDs160, setAnalyzingDs160] = useState(false);

  // Interview state
  const [sessionId, setSessionId] = useState(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [transcript, setTranscript] = useState([]);

  // Video state
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [usingVoiceOnlyMode, setUsingVoiceOnlyMode] = useState(false);
  const avatar = useDidStream();

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [transcribing, setTranscribing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Camera state
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const videoRef = useRef(null);

  // Timer state
  const SESSION_TIME_LIMIT = 240;
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(SESSION_TIME_LIMIT);
  const sessionTimerRef = useRef(null);

  // Completion state
  const [showResults, setShowResults] = useState(false);
  const [finalAssessment, setFinalAssessment] = useState(null);

  // Check access on mount
  useEffect(() => {
    checkInterviewAccess();
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
      }
    };
  }, []);

  // Session timer
  useEffect(() => {
    if (sessionStarted && sessionTimeRemaining > 0) {
      sessionTimerRef.current = setInterval(() => {
        setSessionTimeRemaining(prev => {
          if (prev <= 1) {
            completeInterview();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (sessionTimerRef.current) clearInterval(sessionTimerRef.current);
    };
  }, [sessionStarted]);

  // Recording timer
  useEffect(() => {
    let timer;
    if (isRecording) {
      timer = setInterval(() => setRecordingTime(prev => prev + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [isRecording]);

  // Connect camera stream to video element when both are available
  useEffect(() => {
    if (cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [cameraStream, sessionStarted]);

  const checkInterviewAccess = async () => {
    try {
      const response = await api.get('/subscription/check/interview');
      setHasAccess(response.data.can_use);
      setCredits(response.data.credits || 0);
    } catch (error) {
      setHasAccess(false);
    } finally {
      setCheckingAccess(false);
    }
  };

  const purchaseCredits = async () => {
    navigate('/pricing');
  };

  const initializeCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setCameraStream(stream);
      setCameraEnabled(true);
      
      // The useEffect will handle connecting the stream to the video element
      // when both are available

      const audioStream = new MediaStream(stream.getAudioTracks());
      mediaRecorderRef.current = new MediaRecorder(audioStream, { mimeType: 'audio/webm' });
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        chunksRef.current = [];
      };
      
      return true;
    } catch (error) {
      toast({ title: 'Camera Error', description: 'Could not access camera. Use text mode.', variant: 'destructive' });
      return false;
    }
  };

  // Handle PDF upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      toast({ title: 'Invalid File', description: 'Please upload a PDF', variant: 'destructive' });
      return;
    }

    setDs160File(file);
    setAnalyzingDs160(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/interview/realtime/upload-ds160-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDs160Data(response.data.parsed_data);
      setDs160Analyzed(true);
      toast({ title: 'DS-160 Analyzed!', description: 'Ready to start interview' });
    } catch (error) {
      setDs160File(null);
      toast({ title: 'Upload Error', description: 'Failed to process PDF', variant: 'destructive' });
    } finally {
      setAnalyzingDs160(false);
    }
  };

  // Analyze text DS-160
  const analyzeDs160Text = async () => {
    if (!ds160Content.trim()) {
      toast({ title: 'No Content', description: 'Please paste your DS-160', variant: 'destructive' });
      return;
    }

    setAnalyzingDs160(true);
    try {
      const response = await api.post('/interview/realtime/analyze-ds160', { file_content: ds160Content });
      setDs160Data(response.data.parsed_data);
      setDs160Analyzed(true);
      toast({ title: 'DS-160 Analyzed!', description: 'Ready to start' });
    } catch (error) {
      toast({ title: 'Analysis Error', description: 'Failed to analyze', variant: 'destructive' });
    } finally {
      setAnalyzingDs160(false);
    }
  };

  // Make ALEC speak: live WebRTC avatar first, browser TTS as fallback
  const playInterviewerVideo = async (text) => {
    setVideoLoading(true);
    setVideoError(false);
    try {
      let ok = false;
      try { ok = await avatar.speak(text); } catch (e) { ok = false; }
      if (!ok) {
        setVideoError(true);
        if (!usingVoiceOnlyMode) {
          setUsingVoiceOnlyMode(true);
          toast({
            title: 'Voice Mode Active',
            description: 'Live video is unavailable right now — ALEC will continue with voice.',
            variant: 'default'
          });
        }
        speakTextFallback(text);
      }
    } finally {
      setVideoLoading(false);
    }
  };

  // Browser TTS fallback
  const speakTextFallback = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 0.9; // Lower pitch for male voice
      
      // Try to find a male voice
      const voices = window.speechSynthesis.getVoices();
      const maleVoice = voices.find(v => v.name.includes('Male') || v.name.includes('Daniel') || v.name.includes('Alex') || v.name.includes('Guy'));
      if (maleVoice) utterance.voice = maleVoice;
      
      window.speechSynthesis.speak(utterance);
    }
  };

  // Start interview
  const startInterview = async () => {
    setLoading(true);
    try {
      // Credits are deducted server-side only after the session is created (start protection)
      // Connect the live avatar first (fast), then create the session
      await avatar.connect();
      const response = await api.post('/interview/structured/start', { ds160_data: ds160Data });
      
      setSessionId(response.data.session_id);
      setCurrentQuestion(response.data.current_question);
      setQuestionNumber(1);
      setSessionStarted(true);

      // Add opening to transcript
      const openingMessage = response.data.opening_message;
      setTranscript([{ role: 'interviewer', message: openingMessage }]);

      // Play video of interviewer greeting
      await playInterviewerVideo(openingMessage);

      toast({ title: 'Interview Started', description: 'Good luck!' });
    } catch (error) {
      if (error.response?.status === 402) {
        setHasAccess(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Recording controls
  const startRecording = () => {
    if (!mediaRecorderRef.current) return;
    chunksRef.current = [];
    mediaRecorderRef.current.start();
    setIsRecording(true);
    setRecordingTime(0);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Submit voice response
  const submitVoiceResponse = async () => {
    if (!audioBlob) return;
    
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'response.webm');
      
      const transcribeResponse = await api.post(`/interview/structured/${sessionId}/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      await submitResponse(transcribeResponse.data.text);
    } catch (error) {
      toast({ title: 'Transcription Error', description: 'Failed to process audio', variant: 'destructive' });
    } finally {
      setTranscribing(false);
      setAudioBlob(null);
    }
  };

  // Submit text response
  const submitTextResponse = async () => {
    if (!textInput.trim()) return;
    await submitResponse(textInput);
    setTextInput('');
  };

  // Core response submission
  const submitResponse = async (responseText) => {
    setIsProcessing(true);
      avatar.acknowledge();
    
    // Add user response to transcript
    setTranscript(prev => [...prev, { role: 'user', message: responseText }]);

    try {
      const response = await api.post(`/interview/structured/${sessionId}/respond`, {
        question_id: questionNumber - 1,
        response_text: responseText,
        response_time_seconds: 30
      });

      if (response.data.interview_completed) {
        await completeInterview();
      } else {
        const nextQuestion = response.data.next_question?.question || response.data.transition_message;
        setCurrentQuestion(response.data.next_question);
        setQuestionNumber(response.data.next_question_index + 1);
        
        // Add interviewer's next question to transcript
        setTranscript(prev => [...prev, { role: 'interviewer', message: nextQuestion }]);
        
        // Play video of next question
        await playInterviewerVideo(nextQuestion);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to process response', variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Complete interview
  const completeInterview = async () => {
    setLoading(true);
    try {
      const response = await api.post(`/interview/structured/${sessionId}/complete`, {});
      setFinalAssessment(response.data.assessment);
      setShowResults(true);
      avatar.disconnect();
      
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to get assessment', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Loading state
  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-emerald-500 animate-spin" />
      </div>
    );
  }

  // Paywall - redirect to pricing
  if (!hasAccess && !sessionStarted) {
    // Redirect to pricing page
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="text-center pb-2">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mb-4">
                <Coins className="h-10 w-10 text-white" />
              </div>
              <CardTitle className="text-2xl text-white">Credits Required</CardTitle>
              <p className="text-slate-400">You need {creditsRequired} credits for an interview session</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Credit Balance */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Your Balance</p>
                    <p className="text-white text-2xl font-bold">{credits} credits</p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-400 text-sm">Required</p>
                    <p className="text-amber-400 text-2xl font-bold">{creditsRequired} credits</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-700/50 rounded-lg p-6">
                <h3 className="text-white font-semibold mb-4">What you'll get:</h3>
                <ul className="space-y-3 text-slate-300">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-400" />
                    Real-time AI video interviewer (ALEC)
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-400" />
                    DS-160 personalized questions
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-400" />
                    4-minute timed sessions
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-400" />
                    Comprehensive feedback
                  </li>
                </ul>
              </div>
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full h-14 text-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              >
                <Coins className="h-5 w-5 mr-2" />
                Buy Credits
              </Button>
              <p className="text-center text-slate-500 text-sm">
                Starting at $10 for 12 credits
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Results screen
  if (showResults && finalAssessment) {
    const assessment = finalAssessment.overall_assessment || {};
    const scores = finalAssessment.category_scores || {};
    const section214b = finalAssessment.section_214b_analysis || {};
    const visaFeedback = finalAssessment.visa_specific_feedback || {};
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
        <div className="max-w-4xl mx-auto space-y-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="text-center">
              <Award className="h-16 w-16 mx-auto text-amber-400 mb-4" />
              <CardTitle className="text-2xl text-white">Interview Complete!</CardTitle>
              {assessment.visa_type && (
                <Badge className="mt-2 bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                  {assessment.visa_type} Visa Interview
                </Badge>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Risk Score */}
              <div className="text-center p-6 bg-slate-700/50 rounded-xl">
                <p className="text-slate-400 mb-2">Denial Risk Score</p>
                <p className={`text-5xl font-bold ${assessment.risk_score <= 40 ? 'text-emerald-400' : assessment.risk_score <= 60 ? 'text-amber-400' : 'text-red-400'}`}>
                  {assessment.risk_score || 50}/100
                </p>
                <Badge className={`mt-2 ${assessment.recommendation?.includes('APPROVE') ? 'bg-emerald-500' : assessment.recommendation?.includes('DENY') ? 'bg-red-500' : 'bg-amber-500'}`}>
                  {assessment.recommendation || 'BORDERLINE'}
                </Badge>
                <p className="text-slate-300 mt-4">{assessment.summary}</p>
              </div>

              {/* Section 214(b) Analysis */}
              {section214b.score && (
                <div className="bg-slate-700/30 p-5 rounded-xl border border-slate-600">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-400" />
                    Section 214(b) — Non-Immigrant Intent Analysis
                  </h3>
                  <div className="grid md:grid-cols-3 gap-3 mb-3">
                    <div className="bg-slate-800/50 p-3 rounded-lg text-center">
                      <p className="text-slate-400 text-xs">Intent Score</p>
                      <p className={`text-2xl font-bold ${section214b.score >= 7 ? 'text-emerald-400' : section214b.score >= 5 ? 'text-amber-400' : 'text-red-400'}`}>{section214b.score}/10</p>
                    </div>
                    <div className="bg-slate-800/50 p-3 rounded-lg">
                      <p className="text-slate-400 text-xs mb-1">Strongest Tie</p>
                      <p className="text-emerald-400 text-sm">{section214b.strongest_tie || 'N/A'}</p>
                    </div>
                    <div className="bg-slate-800/50 p-3 rounded-lg">
                      <p className="text-slate-400 text-xs mb-1">Weakest Area</p>
                      <p className="text-amber-400 text-sm">{section214b.weakest_area || 'N/A'}</p>
                    </div>
                  </div>
                  <p className="text-slate-300 text-sm">{section214b.assessment}</p>
                </div>
              )}

              {/* Category Scores */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(scores).map(([key, data]) => (
                  <div key={key} className="bg-slate-700/30 p-4 rounded-lg">
                    <p className="text-slate-400 text-sm capitalize">{key.replace(/_/g, ' ')}</p>
                    <p className={`text-2xl font-bold ${data.score >= 7 ? 'text-emerald-400' : data.score >= 5 ? 'text-amber-400' : 'text-red-400'}`}>{data.score}/10</p>
                    <p className="text-slate-400 text-xs mt-1">{data.feedback}</p>
                  </div>
                ))}
              </div>

              {/* Visa-Specific Feedback */}
              {visaFeedback.critical_weakness && (
                <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-lg">
                  <h3 className="text-red-400 font-semibold mb-2">Critical Weakness</h3>
                  <p className="text-slate-300">{visaFeedback.critical_weakness}</p>
                </div>
              )}

              {/* Improvement Tips */}
              {finalAssessment.improvement_tips && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 p-4 rounded-lg">
                  <h3 className="text-emerald-400 font-semibold mb-2">Tips for Improvement</h3>
                  <ul className="space-y-2">
                    {finalAssessment.improvement_tips.map((tip, i) => (
                      <li key={i} className="text-slate-300 flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-400 mt-1 flex-shrink-0" />
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Sample Better Answers */}
              {finalAssessment.sample_better_answers && finalAssessment.sample_better_answers.length > 0 && (
                <div className="bg-blue-500/10 border border-blue-500/30 p-4 rounded-lg">
                  <h3 className="text-blue-400 font-semibold mb-3">How to Answer Better</h3>
                  {finalAssessment.sample_better_answers.map((sample, i) => (
                    <div key={i} className="mb-4 last:mb-0">
                      <p className="text-slate-400 text-sm">Q: {sample.question}</p>
                      <p className="text-red-300 text-sm mt-1">Issue: {sample.their_answer_issue}</p>
                      <p className="text-emerald-300 text-sm mt-1">Better: {sample.better_approach}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-4">
                <Button onClick={() => navigate('/interview')} variant="outline" className="flex-1">
                  Back to Interview Prep
                </Button>
                <Button onClick={() => window.location.reload()} className="flex-1 bg-emerald-500">
                  <RefreshCw className="h-4 w-4 mr-2" /> Practice Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // DS-160 Setup screen
  if (!sessionStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-2xl text-white flex items-center gap-2">
                <FileText className="h-6 w-6 text-emerald-400" />
                Upload Your DS-160
              </CardTitle>
              <p className="text-slate-400">We'll analyze your application to generate interview questions specific to your visa type (F1, B1/B2, H1B, J1, L1, O1, K1, and more)</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Upload Option */}
              <div className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center">
                <Upload className="h-12 w-12 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-300 mb-4">Upload DS-160 Confirmation PDF</p>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="ds160-upload"
                />
                <label htmlFor="ds160-upload">
                  <Button asChild disabled={analyzingDs160}>
                    <span>
                      {analyzingDs160 ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                      {ds160File ? ds160File.name : 'Choose File'}
                    </span>
                  </Button>
                </label>
              </div>

              <div className="text-center text-slate-500">— OR —</div>

              {/* Paste Option */}
              <div>
                <p className="text-slate-300 mb-2">Paste DS-160 Information</p>
                <Textarea
                  value={ds160Content}
                  onChange={(e) => setDs160Content(e.target.value)}
                  placeholder="Paste your DS-160 confirmation details here..."
                  className="min-h-[150px] bg-slate-700 border-slate-600 text-white"
                />
                <Button
                  onClick={analyzeDs160Text}
                  disabled={analyzingDs160 || !ds160Content.trim()}
                  className="mt-3"
                >
                  {analyzingDs160 ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Analyze DS-160
                </Button>
              </div>

              {/* Start Button */}
              {ds160Analyzed && (
                <div className="pt-4 border-t border-slate-700">
                  <div className="flex items-center gap-2 text-emerald-400 mb-4">
                    <CheckCircle className="h-5 w-5" />
                    <span>DS-160 analyzed successfully!</span>
                  </div>
                  <Button
                    onClick={async () => { 
                      await initializeCamera(); 
                      startInterview(); 
                    }}
                    className="w-full h-14 text-lg bg-gradient-to-r from-emerald-500 to-teal-500"
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Camera className="h-5 w-5 mr-2" />}
                    Enable Camera & Start
                  </Button>
                  <Button
                    onClick={startInterview}
                    variant="outline"
                    className="w-full mt-3"
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                    Start Without Camera
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main Interview Screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <img 
              src={AI_PERSONAS.ALEC.avatar} 
              alt="ALEC"
              className="w-12 h-12 rounded-full object-cover border-2 border-emerald-500"
            />
            <div>
              <h1 className="text-xl font-bold text-white">ALEC - AI Visa Officer</h1>
              <p className="text-slate-400 text-sm">Question {questionNumber}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className={`px-4 py-2 rounded-lg ${sessionTimeRemaining <= 60 ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
              <Timer className="h-4 w-4 inline mr-2" />
              <span className="font-mono text-xl">{formatTime(sessionTimeRemaining)}</span>
            </div>
            <Badge className="bg-red-500 animate-pulse">LIVE</Badge>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Interviewer Panel */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
                {/* Live WebRTC avatar (always mounted so the media track can attach) */}
                <LiveAvatar avatar={avatar} />

                {(avatar.status === 'connecting' || avatar.status === 'reconnecting') && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 z-10">
                    <Loader2 className="h-12 w-12 text-emerald-400 animate-spin" />
                    <p className="text-white text-sm mt-2">{avatar.status === 'reconnecting' ? 'Reconnecting live avatar…' : 'Connecting live avatar…'}</p>
                  </div>
                )}

                {avatar.isLive && (
                  <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 bg-red-600/90 px-2.5 py-1 rounded-full" data-testid="alec-live-badge">
                    <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                    <span className="text-white text-xs font-semibold">LIVE</span>
                  </div>
                )}
                {avatar.isSpeaking && (
                  <Badge className="absolute bottom-3 right-3 z-20 bg-emerald-500 text-white animate-pulse">
                    <Volume2 className="h-3 w-3 mr-1" />Speaking
                  </Badge>
                )}
                
                {/* Voice-Only Mode Banner */}
                {usingVoiceOnlyMode && !avatar.isLive && (
                  <div className="absolute top-3 left-3 right-3 z-20">
                    <div className="bg-emerald-500/90 backdrop-blur-sm text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm">
                      <Volume2 className="h-4 w-4" />
                      <span className="font-medium">Voice Mode</span>
                      <span className="text-emerald-100">• ALEC is speaking</span>
                    </div>
                  </div>
                )}
                
                {avatar.isLive ? null : usingVoiceOnlyMode ? (
                  /* Voice-Only Mode Display */
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/30">
                        <Volume2 className="h-12 w-12 text-white animate-pulse" />
                      </div>
                      {/* Sound wave animation rings */}
                      <div className="absolute inset-0 rounded-full border-2 border-emerald-400/30 animate-ping" style={{ animationDuration: '2s' }} />
                      <div className="absolute inset-0 scale-125 rounded-full border border-emerald-400/20 animate-ping" style={{ animationDuration: '2.5s' }} />
                    </div>
                    <p className="text-emerald-400 font-semibold text-lg">Interview in Progress</p>
                    <p className="text-slate-400 text-sm mt-1">Listen to ALEC's questions</p>
                    <div className="mt-4 px-4 py-2 bg-amber-500/20 rounded-lg border border-amber-500/30">
                      <p className="text-amber-400 text-xs text-center">
                        Video temporarily unavailable • Voice mode active
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <img
                      src={avatar.presenterImage || "https://images.unsplash.com/photo-1762522926157-bcc04bf0b10a?auto=format&fit=crop&w=600&q=80"}
                      alt="Interviewer"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>
              
              {/* Current Question */}
              <div className="mt-4 p-4 bg-slate-700/50 rounded-lg">
                <p className="text-slate-400 text-sm mb-1">Current Question:</p>
                <p className="text-white text-lg">{currentQuestion?.question || 'Waiting for interviewer...'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Applicant Panel */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              {/* Camera Feed */}
              <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden mb-4">
                {cameraEnabled && cameraStream ? (
                  <video 
                    ref={(el) => {
                      videoRef.current = el;
                      if (el && cameraStream && el.srcObject !== cameraStream) {
                        el.srcObject = cameraStream;
                      }
                    }} 
                    autoPlay 
                    muted 
                    playsInline 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-500 flex-col gap-2">
                    <Camera className="h-12 w-12" />
                    <span className="text-sm">Camera not enabled</span>
                  </div>
                )}
              </div>

              {/* Response Input */}
              <div className="space-y-4">
                {/* Voice Recording */}
                <div className="flex items-center gap-3">
                  {!isRecording ? (
                    <Button onClick={startRecording} disabled={!cameraEnabled || isProcessing} className="flex-1 bg-red-500 hover:bg-red-600">
                      <Mic className="h-5 w-5 mr-2" /> Start Speaking
                    </Button>
                  ) : (
                    <Button onClick={stopRecording} className="flex-1 bg-amber-500 hover:bg-amber-600">
                      <StopCircle className="h-5 w-5 mr-2" /> Stop ({recordingTime}s)
                    </Button>
                  )}
                  
                  {audioBlob && (
                    <Button onClick={submitVoiceResponse} disabled={transcribing || isProcessing} className="bg-emerald-500">
                      {transcribing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                    </Button>
                  )}
                </div>

                <div className="text-center text-slate-500 text-sm">or type your response</div>

                {/* Text Input */}
                <div className="flex gap-2">
                  <Textarea
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder="Type your answer..."
                    className="flex-1 bg-slate-700 border-slate-600 text-white"
                    disabled={isProcessing}
                  />
                  <Button
                    onClick={submitTextResponse}
                    disabled={!textInput.trim() || isProcessing}
                    className="bg-emerald-500"
                  >
                    {isProcessing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  </Button>
                </div>

                {/* End Interview */}
                <Button onClick={completeInterview} variant="outline" className="w-full" disabled={loading}>
                  End Interview & Get Feedback
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Transcript */}
        <Card className="mt-6 bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-lg">Interview Transcript</CardTitle>
          </CardHeader>
          <CardContent className="max-h-48 overflow-y-auto">
            {transcript.map((entry, i) => (
              <div key={i} className={`mb-3 ${entry.role === 'interviewer' ? 'text-emerald-400' : 'text-blue-400'}`}>
                <span className="font-semibold">{entry.role === 'interviewer' ? 'Officer: ' : 'You: '}</span>
                <span className="text-slate-300">{entry.message}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StructuredInterview;
